/**
 * GSTR9New FetchReconcile Reducer
 */

import produce from 'immer';
import {
  FETCH_RECORDS_REQUEST,
  FETCH_RECORDS_SUCCESS,
  FETCH_RECORDS_FAILURE,
  RECONCILE_RECORDS_REQUEST,
  RECONCILE_RECORDS_SUCCESS,
  RECONCILE_RECORDS_FAILURE,
  GET_FETCH_HISTORY_REQUEST,
  GET_FETCH_HISTORY_SUCCESS,
  GET_FETCH_HISTORY_FAILURE,
  GET_RECONCILE_DATA_HISTORY_REQUEST,
  GET_RECONCILE_DATA_HISTORY_SUCCESS,
  GET_RECONCILE_DATA_HISTORY_FAILURE,
  SET_SELECTED_RETURN_PERIOD,
  SET_SELECTED_GSTIN,
  SET_FETCH_PROGRESS,
  SET_RECONCILE_PROGRESS,
  SET_PAGINATION_INFO,
  SET_CURRENT_PAGE,
  SET_PAGE_SIZE,
  SET_FETCH_PAGINATION_INFO,
  SET_RECONCILE_PAGINATION_INFO,
  SET_FETCH_PAGE_SIZE,
  SET_RECONCILE_PAGE_SIZE,
  CLEAR_FETCH_HISTORY,
  CLEAR_RECONCILE_HISTORY,
  CLEAR_ALL_DATA,
} from './constants';

// Import common constants
import {
  LOADER_STATUS,
} from '../common/constants';

// The initial state of the FetchReconcile
export const initialState = {
  loaderStatus: false,
  selectedGSTIN: '',
  selectedReturnPeriod: '',
  fetchProgress: 0,
  reconcileProgress: 0,
  fetchedRecords: null,
  reconciledRecords: null,
  fetchHistory: [],
  reconcileHistory: [], // Separate storage for reconcile data history
  fetchError: null,
  reconcileError: null,
  historyError: null,
  isFetching: false,
  isReconciling: false,
  isLoadingHistory: false,
  // Pagination state for fetch history
  fetchPagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    pageSize: 10,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  // Pagination state for reconcile history
  reconcilePagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    pageSize: 10,
    hasNextPage: false,
    hasPreviousPage: false,
  },
};

/* eslint-disable default-case, no-param-reassign */
const fetchReconcileReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case LOADER_STATUS:
        draft.loaderStatus = action.loaderStatus;
        break;
      
      case SET_SELECTED_GSTIN:
        draft.selectedGSTIN = action.gstin;
        break;
      
      case SET_SELECTED_RETURN_PERIOD:
        draft.selectedReturnPeriod = action.returnPeriod;
        break;
      
      case SET_FETCH_PROGRESS:
        draft.fetchProgress = action.progress;
        break;
      
      case SET_RECONCILE_PROGRESS:
        draft.reconcileProgress = action.progress;
        break;
      
      case FETCH_RECORDS_REQUEST:
        draft.isFetching = true;
        draft.fetchError = null;
        break;
      
      case FETCH_RECORDS_SUCCESS:
        draft.isFetching = false;
        draft.fetchedRecords = action.payload;
        draft.fetchError = null;
        break;
      
      case FETCH_RECORDS_FAILURE:
        draft.isFetching = false;
        draft.fetchError = action.payload;
        break;
      
      case RECONCILE_RECORDS_REQUEST:
        draft.isReconciling = true;
        draft.reconcileError = null;
        break;
      
      case RECONCILE_RECORDS_SUCCESS:
        draft.isReconciling = false;
        draft.reconciledRecords = action.payload;
        draft.reconcileError = null;
        break;
      
      case RECONCILE_RECORDS_FAILURE:
        draft.isReconciling = false;
        draft.reconcileError = action.payload;
        break;
      
      case GET_FETCH_HISTORY_REQUEST:
        draft.isLoadingHistory = true;
        draft.historyError = null;
        break;
      
      case GET_FETCH_HISTORY_SUCCESS:
        draft.isLoadingHistory = false;
        draft.fetchHistory = action.payload;
        draft.historyError = null;
        break;
      
      case GET_FETCH_HISTORY_FAILURE:
        draft.isLoadingHistory = false;
        draft.historyError = action.payload;
        break;
      
      case GET_RECONCILE_DATA_HISTORY_REQUEST:
        draft.isLoadingHistory = true;
        draft.historyError = null;
        break;
      
      case GET_RECONCILE_DATA_HISTORY_SUCCESS:
        draft.isLoadingHistory = false;
        draft.reconcileHistory = action.payload;
        draft.historyError = null;
        break;
      
      case GET_RECONCILE_DATA_HISTORY_FAILURE:
        draft.isLoadingHistory = false;
        draft.historyError = action.payload;
        break;
      
      case SET_PAGINATION_INFO:
        draft.fetchPagination = action.paginationInfo;
        break;
      
      case SET_FETCH_PAGINATION_INFO:
        draft.fetchPagination = action.paginationInfo;
        break;
      
      case SET_RECONCILE_PAGINATION_INFO:
        draft.reconcilePagination = action.paginationInfo;
        break;
      
      case SET_CURRENT_PAGE:
        draft.pagination.currentPage = action.page;
        break;
      
      case SET_PAGE_SIZE:
        draft.pagination.pageSize = action.pageSize;
        break;
      
      case SET_FETCH_PAGE_SIZE:
        draft.fetchPagination.pageSize = action.pageSize;
        break;
      
      case SET_RECONCILE_PAGE_SIZE:
        draft.reconcilePagination.pageSize = action.pageSize;
        break;
      
      case CLEAR_FETCH_HISTORY:
        draft.fetchHistory = [];
        draft.fetchedRecords = null;
        draft.fetchError = null;
        break;
      
      case CLEAR_RECONCILE_HISTORY:
        draft.reconcileHistory = [];
        draft.reconciledRecords = null;
        draft.reconcileError = null;
        break;
      
      case CLEAR_ALL_DATA:
        draft.fetchHistory = [];
        draft.reconcileHistory = [];
        draft.fetchedRecords = null;
        draft.reconciledRecords = null;
        draft.fetchError = null;
        draft.reconcileError = null;
        draft.historyError = null;
        draft.isFetching = false;
        draft.isReconciling = false;
        draft.isLoadingHistory = false;
        draft.fetchProgress = 0;
        draft.reconcileProgress = 0;
        // Reset pagination
        draft.pagination = {
          currentPage: 1,
          pageSize: 10,
          totalRecords: 0,
          totalPages: 0
        };
        draft.fetchPagination = {
          currentPage: 1,
          pageSize: 10,
          totalRecords: 0,
          totalPages: 0
        };
        draft.reconcilePagination = {
          currentPage: 1,
          pageSize: 10,
          totalRecords: 0,
          totalPages: 0
        };
        break;
    }
  });

export default fetchReconcileReducer;
